import { router } from "@inertiajs/react"
import i18n from "i18next"
import { initReactI18next } from "react-i18next"

import en from "./locales/en.json"

export const SUPPORTED_LOCALES = ["en", "es", "pt", "fr", "de", "ro"] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

type LocaleMessages = typeof en

const localeLoaders: Record<
  SupportedLocale,
  () => Promise<{ default: LocaleMessages }>
> = {
  en: () => Promise.resolve({ default: en }),
  es: () => import("./locales/es.json"),
  pt: () => import("./locales/pt.json"),
  fr: () => import("./locales/fr.json"),
  de: () => import("./locales/de.json"),
  ro: () => import("./locales/ro.json"),
}

const loadedLocales = new Set<SupportedLocale>(["en"])
const loadingLocales = new Map<SupportedLocale, Promise<void>>()

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
  },
  lng: "en",
  fallbackLng: "en",
  partialBundledLanguages: true,
  interpolation: { escapeValue: false },
  returnNull: false,
})

function isSupportedLocale(locale: string): locale is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(locale)
}

/** Synchronously register locale messages (used by SSR to preload bundles). */
export function registerLocaleResources(
  locales: Partial<Record<SupportedLocale, LocaleMessages>>,
) {
  for (const [locale, messages] of Object.entries(locales) as [
    SupportedLocale,
    LocaleMessages,
  ][]) {
    if (!messages || loadedLocales.has(locale)) continue
    i18n.addResourceBundle(locale, "translation", messages, true, true)
    loadedLocales.add(locale)
  }
}

export async function ensureLocaleLoaded(locale: string): Promise<void> {
  if (!isSupportedLocale(locale) || loadedLocales.has(locale)) return

  const inflight = loadingLocales.get(locale)
  if (inflight) {
    await inflight
    return
  }

  const loadPromise = localeLoaders[locale]()
    .then((mod) => {
      i18n.addResourceBundle(locale, "translation", mod.default, true, true)
      loadedLocales.add(locale)
    })
    .finally(() => {
      loadingLocales.delete(locale)
    })

  loadingLocales.set(locale, loadPromise)
  await loadPromise
}

async function applyLocale(locale: unknown) {
  if (typeof locale !== "string" || !isSupportedLocale(locale)) return

  await ensureLocaleLoaded(locale)
  if (i18n.language !== locale) {
    await i18n.changeLanguage(locale)
  }
}

export async function applyLocaleFromPageProps(
  props: Record<string, unknown>,
) {
  await applyLocale(props.locale)
}

// Keep i18n in sync with Inertia navigation (locale comes from shared props).
if (typeof window !== "undefined") {
  router.on("navigate", (event) => {
    const page = event.detail.page as { props?: Record<string, unknown> }
    void applyLocale(page.props?.locale)
  })
}

export default i18n
